/**
 * Forgot Password Form Component
 *
 * This component provides a multi-step password reset flow:
 * 1. Email entry - user enters their email
 * 2. TOTP verification - user enters the 6-digit code sent to email
 * 3. New password - user enters and confirms new password
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from './AuthProvider';

export type ForgotPasswordStep = 'email' | 'totp' | 'password' | 'success';

interface ForgotPasswordFormProps {
  initialEmail?: string;
  onSuccess: () => void;
  onCancel: () => void;
  /** Controlled step - if provided, component uses this instead of internal state */
  step?: ForgotPasswordStep;
  /** Callback when step changes - required if step prop is provided */
  onStepChange?: (step: ForgotPasswordStep) => void;
}

const stepLabels = ['Enter Email', 'Verify Code', 'New Password', 'Done'];

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  initialEmail = '',
  onSuccess,
  onCancel,
  step: controlledStep,
  onStepChange,
}) => {
  const { forgotPassword, verifyResetCode, resetPassword, isLoading, error, clearError } = useAuth();
  const theme = useTheme();

  // Internal state for uncontrolled mode
  const [internalStep, setInternalStep] = useState<ForgotPasswordStep>('email');

  // Use controlled step if provided, otherwise use internal state
  const currentStep = controlledStep ?? internalStep;
  const setCurrentStep = (newStep: ForgotPasswordStep) => {
    if (onStepChange) {
      onStepChange(newStep);
    } else {
      setInternalStep(newStep);
    }
  };
  const [email, setEmail] = useState(initialEmail);
  const [totpCode, setTotpCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Debug: Track mount/unmount
  useEffect(() => {
    console.log('ForgotPasswordForm - MOUNTED, currentStep:', currentStep, 'controlledStep:', controlledStep);
    return () => {
      console.log('ForgotPasswordForm - UNMOUNTING');
    };
  }, []);

  // Debug: Track step changes
  useEffect(() => {
    console.log('ForgotPasswordForm - Step changed to:', currentStep);
  }, [currentStep]);

  // Clear errors when changing steps
  useEffect(() => {
    setLocalError(null);
    clearError();
  }, [currentStep, clearError]);

  // Get step index for stepper
  const getStepIndex = () => {
    switch (currentStep) {
      case 'email':
        return 0;
      case 'totp':
        return 1;
      case 'password':
        return 2;
      case 'success':
        return 3;
      default:
        return 0;
    }
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('ForgotPasswordForm - handleEmailSubmit called, currentStep:', currentStep);
    setLocalError(null);
    setEmailError(null);

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const result = await forgotPassword(email);
    console.log('ForgotPasswordForm - forgotPassword result:', result);
    if (result.success) {
      console.log('ForgotPasswordForm - Calling setCurrentStep("totp")');
      setCurrentStep('totp');
      console.log('ForgotPasswordForm - setCurrentStep called');
    } else {
      setLocalError(result.error || 'Failed to send verification code');
    }
  };

  // Handle TOTP input change
  const handleTotpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...totpCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });
      setTotpCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    } else if (/^\d$/.test(value) || value === '') {
      const newCode = [...totpCode];
      newCode[index] = value;
      setTotpCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle TOTP keydown
  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle TOTP verification (verify code before moving to password step)
  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const code = totpCode.join('');
    if (code.length !== 6) {
      setLocalError('Please enter all 6 digits');
      return;
    }

    // Verify the TOTP code with the backend
    const result = await verifyResetCode(email, code);
    console.log('ForgotPasswordForm - verifyResetCode result:', result);

    if (result.success) {
      // Code verified, move to password step
      setCurrentStep('password');
    } else {
      // Show error to user
      setLocalError(result.error || 'Invalid verification code');
    }
  };

  // Handle password submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setPasswordError(null);

    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // TOTP was already verified in the previous step, now just reset password
    const result = await resetPassword(email, newPassword);

    if (result.success) {
      setCurrentStep('success');
    } else {
      setLocalError(result.error || 'Failed to reset password');
    }
  };

  // Render email step
  const renderEmailStep = () => (
    <Fade in timeout={500}>
      <Box component="form" onSubmit={handleEmailSubmit}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a verification code to reset your password.
        </Typography>

        <TextField
          fullWidth
          type="email"
          label="Email Address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(null);
          }}
          placeholder="Enter your email address"
          disabled={isLoading}
          autoComplete="email"
          autoFocus
          error={!!emailError}
          helperText={emailError}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        {(localError || error) && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {localError || error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
            startIcon={<ArrowBackIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Back to Login
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            {isLoading ? 'Sending...' : 'Send Code'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  // Render TOTP step
  const renderTotpStep = () => (
    <Fade in timeout={500}>
      <Box component="form" onSubmit={handleTotpSubmit}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We've sent a 6-digit verification code to <strong>{email}</strong>. Enter it below.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
          {totpCode.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleTotpChange(index, e.target.value)}
              onKeyDown={(e) => handleTotpKeyDown(index, e)}
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  padding: '12px',
                },
              }}
              sx={{
                width: 48,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              disabled={isLoading}
              autoFocus={index === 0}
            />
          ))}
        </Box>

        {(localError || error) && (
          <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: 'center' }}>
            {localError || error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setCurrentStep('email')}
            disabled={isLoading}
            startIcon={<ArrowBackIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || totpCode.join('').length !== 6}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            {isLoading ? 'Verifying...' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  // Render password step
  const renderPasswordStep = () => (
    <Fade in timeout={500}>
      <Box component="form" onSubmit={handlePasswordSubmit}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a new password for your account.
        </Typography>

        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="New Password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setPasswordError(null);
          }}
          placeholder="Enter new password"
          disabled={isLoading}
          autoComplete="new-password"
          autoFocus
          error={!!passwordError}
          helperText={passwordError || 'At least 8 characters'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isLoading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        <TextField
          fullWidth
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setPasswordError(null);
          }}
          placeholder="Confirm new password"
          disabled={isLoading}
          autoComplete="new-password"
          error={!!passwordError && newPassword !== confirmPassword}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        {(localError || error) && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {localError || error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setCurrentStep('totp')}
            disabled={isLoading}
            startIcon={<ArrowBackIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  // Render success step
  const renderSuccessStep = () => (
    <Fade in timeout={500}>
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            mb: 3,
          }}
        >
          <CheckIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
        </Box>

        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Password Reset Successful!
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Your password has been reset successfully. You can now sign in with your new password.
        </Typography>

        <Button
          variant="contained"
          onClick={onSuccess}
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          Go to Sign In
        </Button>
      </Box>
    </Fade>
  );

  return (
    <Box>
      {/* Progress Stepper */}
      <Stepper activeStep={getStepIndex()} sx={{ mb: 4 }}>
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      {currentStep === 'email' && renderEmailStep()}
      {currentStep === 'totp' && renderTotpStep()}
      {currentStep === 'password' && renderPasswordStep()}
      {currentStep === 'success' && renderSuccessStep()}
    </Box>
  );
};

export default ForgotPasswordForm;
